import * as React from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { GradientText } from "./GradientText";
import { ArrowRight, Calendar } from "lucide-react";
import { isLocalDev } from "@/lib/is-local-dev";

interface FinalCTAProps {
  headline: string;
  description: string;
  primaryCTA: { text: string };
  secondaryCTA?: { text: string; onClick?: () => void };
}

export function FinalCTA({
  headline,
  description,
  primaryCTA,
  secondaryCTA,
}: FinalCTAProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 lg:py-32 relative overflow-hidden"
    >
      {/* Subtle background */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-accent/5 to-transparent" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={cn(
            "max-w-3xl mx-auto text-center",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-6">
            <GradientText>{headline}</GradientText>
          </h2>

          <p className="text-muted-foreground text-base lg:text-lg mb-10 max-w-xl mx-auto">
            {description}
          </p>

          <div
            className={cn(
              "flex flex-col sm:flex-row items-center justify-center gap-4",
              "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
          >
            <Link to={isLocalDev ? "/signup" : "/login"}>
              <Button size="lg" className="h-12 px-8 text-base rounded-full">
                {primaryCTA.text}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>

            {secondaryCTA && (
              <Button
                variant="ghost"
                size="lg"
                onClick={secondaryCTA.onClick}
                className="h-12 px-8 text-base rounded-full text-foreground hover:text-foreground hover:bg-muted"
              >
                <Calendar className="w-4 h-4 mr-2" />
                {secondaryCTA.text}
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
